import React, { FC } from 'react';

import { Center, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import {
  ActionButton,
  DeleteActionButton,
  EditIcon,
  IconTypes,
  StackedCell,
  WithResourceIcon,
} from 'components/index';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';
import { formatDate } from 'utils/date';

interface Props {}

export const ActivityGroupsTable: FC<Props> = props => {
  const { activityGroups, activityGroupAction } = useSelector(
    selectIncidentResponse,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const columns: TableColumnProps[] = [
    {
      header: 'Resource',
      render: ({ row }) => (
        <WithResourceIcon resourceType={IconTypes.SuddenActivity}>
          <StackedCell
            upper={row.name ?? '-'}
            lower="Activity Group"
            showLowerTooltip={false}
            showUpperTooltip={false}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Description',
      render: ({ row }) => (
        <Text whiteSpace="wrap" noOfLines={2}>
          {row.description ?? '-'}
        </Text>
      ),
      align: 'left',
    },
    {
      header: 'Resources',
      render: ({ row }) => row.bucket_count ?? 0,
    },
    {
      header: 'Label',
      render: ({ row }) => row.label ?? '-',
      align: 'left',
    },
    {
      header: 'Created',
      render: ({ row }) => (
        <StackedCell
          upper={formatDate(
            row.created_at ? String(row.created_at) : undefined,
          )}
          lower={
            row?.created_by === 'ariksa' ? 'Ariksa' : row.created_by ?? '-'
          }
          showLowerTooltip={false}
          showUpperTooltip={false}
        />
      ),
      align: 'left',
    },
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center onClick={e => e.stopPropagation()}>
          <ActionButton
            icon={<EditIcon />}
            label="Edit"
            onClick={event => {
              event.stopPropagation();
              navigate(`/visibility/activity/group/edit/${row?.id}`);
            }}
            isDisabled={row?.created_by === 'ariksa'}
          />
          <DeleteActionButton
            name={row?.name ?? ''}
            type="activity group"
            isDisabled={row?.created_by === 'ariksa'}
            isLoading={activityGroupAction.isLoading}
            onConfirm={() =>
              dispatch(
                actions.deleteActivityGroup({
                  q: { activityGroupId: row?.id },
                  onSuccess: () =>
                    dispatch(
                      actions.getActivityGroups({
                        q: {
                          offset:
                            (activityGroups.page.info.page_number - 1) *
                            activityGroups.page.info.page_size,
                          limit: activityGroups.page.info.page_size,
                        },
                      }),
                    ),
                }),
              )
            }
          />
        </Center>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={activityGroups.data}
        isError={activityGroups.isError}
        isLoading={activityGroups.isLoading}
        pagination={{
          pageInfo: activityGroups.page.info,
          totalCount: activityGroups.page.totalCount,
          onChange: info =>
            dispatch(actions.updateActivityGroupsPagination(info)),
        }}
        onRowClick={row =>
          navigate(
            '/visibility/activity/group/' +
              row?.id +
              '?tab=Data&groupName=' +
              row?.name,
          )
        }
        cursor="pointer"
      />
    </>
  );
};
